import React, { useState, useCallback, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Plus,
  FileSpreadsheet,
  LogOut,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  MessageSquare,
  Home,
  Settings,
  Globe,
  Check,
  Sun,
  Moon,
} from "lucide-react";
import { useTheme } from "next-themes";
import { Logo } from "@/shared/components/Logo";
import { useAuth } from "@/features/auth";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  useToast,
} from "@/shared/components/ui";
import { useProjectsQuery } from "@/features/project/hooks/useProjectQuery";
import { useSessionsQuery } from "@/features/session/hooks/useSessionQuery";
import { cn, getUserDisplayName } from "@/shared/lib/utils";
import { UserAvatar } from "@/shared/components/UserAvatar";
import { DreAnalysisDialog } from "@/features/dre";
import { useIsAdmin } from "@/features/admin/hooks/useIsAdmin";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGUAGES } from "@/shared/lib/i18n";
import { isFeatureEnabled } from "@/shared/lib/featureFlags";
import { resolveProjectVisuals } from "@/features/project/lib/projectVisuals";
import type { Project } from "@/shared/types/session";

interface SidebarProps {
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
  mobileMode?: boolean;
  onNavigate?: () => void;
}

export function Sidebar({ collapsed = false, onCollapsedChange, mobileMode = false, onNavigate }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const { toast } = useToast();
  const { data: projects = [], isLoading: loadingProjects } = useProjectsQuery();
  const [openDre, setOpenDre] = useState(false);
  const isAdmin = useIsAdmin();
  const { t, i18n } = useTranslation("sidebar");
  const showDreAnalysis = isFeatureEnabled("DRE_ANALYSIS");
  const { resolvedTheme, setTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  const handleThemeToggle = useCallback(() => {
    setTheme(isDark ? "light" : "dark");
  }, [isDark, setTheme]);

  // Match projectId from /project/:id - captures any ID format (numeric or UUID)
  const currentProjectId = useMemo(() =>
    location.pathname.match(/\/project\/([^/]+)/)?.[1],
    [location.pathname]
  );

  // Match sessionId from /project/:id/session/:sessionId
  const currentSessionId = useMemo(() =>
    location.pathname.match(/\/session\/([^/]+)/)?.[1],
    [location.pathname]
  );

  // isExpanded: true only after the sidebar width transition finishes.
  // Text is hidden immediately on collapse; shown only after onTransitionEnd fires on open.
  // No useEffect or timers — driven purely by CSS transition events and render-time state.
  const [prevCollapsed, setPrevCollapsed] = useState(collapsed);
  const [hasAnimatedOpen, setHasAnimatedOpen] = useState(!collapsed);

  if (prevCollapsed !== collapsed) {
    setPrevCollapsed(collapsed);
    if (collapsed) setHasAnimatedOpen(false);
  }

  const isExpanded = !collapsed && hasAnimatedOpen;

  // Expanded projects state - tracks which projects have their sessions list open
  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set());

  const toggleProjectExpanded = useCallback((projectId: string) => {
    setExpandedProjects((prev) => {
      const next = new Set(prev);
      if (next.has(projectId)) {
        next.delete(projectId);
      } else {
        next.add(projectId);
      }
      return next;
    });
  }, []);

  // Auto-expand the active project when navigating to one of its sessions.
  // Render-time state update: runs synchronously during render instead of deferred via useEffect.
  const [prevSessionId, setPrevSessionId] = useState(currentSessionId);
  if (prevSessionId !== currentSessionId) {
    setPrevSessionId(currentSessionId);
    if (currentProjectId && currentSessionId) {
      setExpandedProjects((prev) => {
        if (prev.has(currentProjectId)) return prev;
        const next = new Set(prev);
        next.add(currentProjectId);
        return next;
      });
    }
  }

  const handleLogout = useCallback(() => {
    logout();
    toast({
      title: t("logoutSuccess"),
      description: t("logoutDescription"),
    });
  }, [logout, toast, t]);

  const handleNewProject = useCallback(() => {
    navigate("/dashboard/new-project");
  }, [navigate]);

  const displayName = useMemo(() => getUserDisplayName(user), [user]);

  const userName = displayName || t("user");

  return (
    <TooltipProvider delayDuration={0}>
      <aside
        className={cn(
          "h-full bg-visagio-teal-dark flex flex-col transition-[width] duration-300 shrink-0 shadow-visagio-lg overflow-hidden",
          collapsed ? "w-16" : "w-64"
        )}
        onTransitionEnd={(e) => {
          if (e.propertyName === "width" && !collapsed) setHasAnimatedOpen(true);
        }}
      >
        {/* Header */}
        <div className={cn(
          "h-16 border-b border-visagio-teal/30 flex items-center px-4",
          collapsed ? "justify-center" : "justify-between"
        )}>
          <Link to="/dashboard" className="flex items-center" onClick={mobileMode ? onNavigate : undefined}>
            <Logo size="sm" showText={isExpanded || mobileMode} />
          </Link>
          {isExpanded && !mobileMode && (
            <div className="animate-in fade-in duration-150">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    className="text-white/70 hover:text-white hover:bg-visagio-teal/50"
                    onClick={() => onCollapsedChange?.(!collapsed)}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right" className="bg-visagio-teal text-white border-none">
                  {t("collapse")}
                </TooltipContent>
              </Tooltip>
            </div>
          )}
        </div>

        {/* Expand button when collapsed (desktop only) */}
        {collapsed && !mobileMode && (
          <div className="px-2 py-3 border-b border-visagio-teal/30">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  className="w-full text-white/70 hover:text-white hover:bg-visagio-teal/50"
                  onClick={() => onCollapsedChange?.(!collapsed)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right" className="bg-visagio-teal text-white border-none">
                {t("expand")}
              </TooltipContent>
            </Tooltip>
          </div>
        )}

        {/* Navigation - flex column to position sections */}
        <nav className="flex-1 flex flex-col py-4 min-h-0">
          {/* Main Actions - FIXED at top */}
          <div className={cn("px-3 space-y-1 shrink-0", collapsed && "px-2")}>
            <SidebarItem
              icon={Home}
              label={t("dashboard")}
              href="/dashboard"
              active={location.pathname === "/dashboard"}
              collapsed={collapsed}
              isExpanded={isExpanded || mobileMode}
              onNavigate={onNavigate}
            />
          </div>

          {/* Projects Section - SCROLLABLE, takes remaining space */}
          <div className="mt-6 flex-1 min-h-0 flex flex-col">
            {(isExpanded || mobileMode) && (
              <div className="px-4 mb-3 flex items-center justify-between animate-in fade-in duration-150">
                <span className="text-xs font-heading font-semibold text-white/60 uppercase tracking-widest">
                  {t("projects")}
                </span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      className="h-6 w-6 text-white/60 hover:text-white hover:bg-visagio-teal/50"
                      onClick={handleNewProject}
                    >
                      <Plus className="h-3.5 w-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent className="bg-visagio-teal text-white border-none">
                    {t("newProject")}
                  </TooltipContent>
                </Tooltip>
              </div>
            )}

            <div
              className={cn(
                "px-3 space-y-1 flex-1 min-h-0 overflow-y-auto scrollbar-elegant",
                collapsed && "px-2"
              )}
            >
              {collapsed && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      className="w-full text-white/60 hover:text-white hover:bg-visagio-teal/50"
                      onClick={handleNewProject}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="bg-visagio-teal text-white border-none">
                    {t("newProject")}
                  </TooltipContent>
                </Tooltip>
              )}

              {loadingProjects ? (
                <div className={cn("py-4 text-center", collapsed && "py-2")}>
                  <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto" />
                </div>
              ) : projects.length > 0 ? (
                projects.map((project) => (
                  <SidebarProjectItem
                    key={project.id}
                    project={project}
                    active={currentProjectId === project.id}
                    currentSessionId={currentProjectId === project.id ? currentSessionId : undefined}
                    collapsed={collapsed}
                    expanded={mobileMode ? expandedProjects.has(project.id) : expandedProjects.has(project.id)}
                    onToggleExpand={() => toggleProjectExpanded(project.id)}
                    onNavigate={onNavigate}
                  />
                ))
              ) : (
                (isExpanded || mobileMode) && (
                  <p className="text-xs text-white/40 px-2 py-2 font-body animate-in fade-in duration-150">
                    {t("noProjectsYet")}
                  </p>
                )
              )}
            </div>
          </div>

            {/* Tools Section - FIXED at bottom of nav (Feature flagged) */}
          {showDreAnalysis && (
            <div className="mt-6 shrink-0">
              {(isExpanded || mobileMode) && (
                <div className="px-4 mb-3 animate-in fade-in duration-150">
                  <span className="text-xs font-heading font-semibold text-white/60 uppercase tracking-widest">
                    {t("tools")}
                  </span>
                </div>
              )}
              <div className={cn("px-3 space-y-1", collapsed && "px-2")}>
                <SidebarItem
                  icon={FileSpreadsheet}
                  label={t("dreAnalysis")}
                  onClick={() => setOpenDre(true)}
                  collapsed={collapsed}
                  isExpanded={isExpanded || mobileMode}
                />
              </div>
            </div>
          )}
        </nav>

        {/* User Section */}
        <div className="border-t border-visagio-teal/30 p-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className={cn(
                  "w-full justify-start gap-3 h-12 text-white hover:bg-visagio-teal/50",
                  collapsed && "justify-center px-0"
                )}
              >
                <UserAvatar
                  name={displayName || user?.email || t("user")}
                  avatarUrl={user?.avatarUrl}
                  className="h-8 w-8 shrink-0 shadow-sm"
                  fallbackClassName="bg-visagio-teal text-white font-heading font-bold text-sm"
                />
                {(isExpanded || mobileMode) && (
                  <span className="truncate font-body text-sm animate-in fade-in duration-150">{userName}</span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align={collapsed ? "center" : "start"}
              side="top"
              className="w-56 bg-white dark:bg-popover border-visagio-off-white dark:border-border shadow-visagio-lg rounded-xl"
            >
              <DropdownMenuLabel className="font-body">
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-heading font-semibold text-visagio-teal-dark dark:text-foreground">{userName}</p>
                  {user?.email && (
                    <p className="text-xs text-visagio-gray dark:text-muted-foreground">{user.email}</p>
                  )}
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-visagio-off-white dark:bg-border" />
              {isAdmin && (
                <DropdownMenuItem
                  onClick={() => navigate("/admin")}
                  className="text-visagio-teal dark:text-foreground cursor-pointer font-body hover:bg-visagio-off-white dark:hover:bg-muted rounded-lg mx-1"
                >
                  <Settings className="h-4 w-4 mr-2" />
                  {t("adminPage")}
                </DropdownMenuItem>
              )}
              {isAdmin && <DropdownMenuSeparator className="bg-visagio-off-white dark:bg-border" />}
              <DropdownMenuSub>
                <DropdownMenuSubTrigger className="text-visagio-teal dark:text-foreground cursor-pointer font-body rounded-lg mx-1 hover:bg-visagio-off-white dark:hover:bg-muted data-[state=open]:bg-visagio-off-white data-[state=open]:text-visagio-teal dark:data-[state=open]:bg-muted focus:bg-visagio-off-white focus:text-visagio-teal dark:focus:bg-muted">
                  <Globe className="h-4 w-4 mr-2" />
                  {t("language")}
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="bg-white dark:bg-popover border-visagio-off-white dark:border-border shadow-visagio-lg rounded-xl min-w-[140px]">
                  {SUPPORTED_LANGUAGES.map((lang) => (
                    <DropdownMenuItem
                      key={lang.code}
                      onClick={() => i18n.changeLanguage(lang.code)}
                      className="cursor-pointer font-body text-foreground hover:bg-visagio-off-white dark:hover:bg-muted rounded-lg mx-1 flex items-center justify-between"
                    >
                      {lang.label}
                      {i18n.language === lang.code && (
                        <Check className="h-4 w-4 text-visagio-teal dark:text-accent ml-2" />
                      )}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
              <DropdownMenuItem
                onClick={handleThemeToggle}
                className="text-visagio-teal dark:text-foreground cursor-pointer font-body hover:bg-visagio-off-white dark:hover:bg-muted rounded-lg mx-1"
              >
                {isDark ? (
                  <Sun className="h-4 w-4 mr-2 text-amber-400 transition-all duration-300" />
                ) : (
                  <Moon className="h-4 w-4 mr-2 transition-all duration-300" />
                )}
                {isDark ? t("switchToLight") : t("switchToDark")}
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-visagio-off-white dark:bg-border" />
              <DropdownMenuItem
                onClick={handleLogout}
                className="text-red-600 dark:text-red-400 cursor-pointer font-body hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg mx-1"
              >
                <LogOut className="h-4 w-4 mr-2" />
                {t("logout")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {showDreAnalysis && <DreAnalysisDialog open={openDre} onOpenChange={setOpenDre} />}
      </aside>
    </TooltipProvider>
  );
}

// Sidebar Item Component - Memoized to prevent unnecessary re-renders
interface SidebarItemProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  href?: string;
  onClick?: () => void;
  active?: boolean;
  collapsed?: boolean;
  isExpanded?: boolean;
  onNavigate?: () => void;
}

const SidebarItem = React.memo(function SidebarItem({ icon: Icon, label, href, onClick, active, collapsed, isExpanded, onNavigate }: SidebarItemProps) {
  const content = (
    <div
      className={cn(
        "flex items-center gap-3 px-3 py-2.5 rounded-xl font-body text-sm font-medium transition-all duration-200 cursor-pointer group",
        active
          ? "bg-visagio-teal text-white shadow-md"
          : "text-white/70 hover:text-white hover:bg-visagio-teal/50",
        collapsed && "justify-center px-2"
      )}
    >
      <Icon className={cn(
        "h-4 w-4 shrink-0 transition-transform duration-200",
        collapsed && "h-5 w-5",
        !active && "group-hover:scale-110"
      )} />
      {isExpanded && (
        <span className="truncate animate-in fade-in duration-150">{label}</span>
      )}
    </div>
  );

  if (collapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          {href ? (
            <Link to={href} onClick={onNavigate}>{content}</Link>
          ) : (
            <button
              type="button"
              onClick={() => { onClick?.(); onNavigate?.(); }}
              className="w-full text-left bg-transparent border-0 p-0"
            >
              {content}
            </button>
          )}
        </TooltipTrigger>
        <TooltipContent side="right" className="bg-visagio-teal text-white border-none font-body">
          {label}
        </TooltipContent>
      </Tooltip>
    );
  }

  if (href) {
    return <Link to={href} onClick={onNavigate}>{content}</Link>;
  }

  return (
    <button
      type="button"
      onClick={() => { onClick?.(); onNavigate?.(); }}
      className="w-full text-left bg-transparent border-0 p-0"
    >
      {content}
    </button>
  );
});

// Sidebar Project Item - Expandable project row that shows its sessions when opened
interface SidebarProjectItemProps {
  project: Project;
  active: boolean;
  currentSessionId?: string;
  collapsed: boolean;
  expanded: boolean;
  onToggleExpand: () => void;
  onNavigate?: () => void;
}

const SidebarProjectItem = React.memo(function SidebarProjectItem({
  project,
  active,
  currentSessionId,
  collapsed,
  expanded,
  onToggleExpand,
  onNavigate,
}: SidebarProjectItemProps) {
  const visuals = resolveProjectVisuals(project.id, project.name, project);
  const ProjectIcon = visuals.Icon;

  // Collapsed sidebar: keep original behavior (no expansion), only tooltip + link to project
  if (collapsed) {
    const content = (
      <Link
        to={`/dashboard/project/${project.id}`}
        className={cn(
          "group relative flex h-10 w-full items-center justify-center rounded-lg transition-all duration-200",
          active ? "bg-visagio-teal text-white shadow-md" : "text-white/70 hover:bg-visagio-teal/50 hover:text-white"
        )}
      >
        <span className={cn("absolute left-1 top-1/2 h-5 w-1 -translate-y-1/2 rounded-full", visuals.accent.bg)} />
        <ProjectIcon className={cn("h-4 w-4 transition-transform duration-200", !active && "group-hover:scale-110")} />
      </Link>
    );

    return (
      <Tooltip>
        <TooltipTrigger asChild>{content}</TooltipTrigger>
        <TooltipContent side="right" className="bg-visagio-teal text-white border-none font-body">
          {project.name}
        </TooltipContent>
      </Tooltip>
    );
  }

  const handleChevronClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onToggleExpand();
  };

  return (
    <div className="flex flex-col">
      <div
        className={cn(
          "group relative flex items-center rounded-lg font-body text-sm font-medium transition-all duration-200",
          active
            ? "bg-visagio-teal text-white shadow-md"
            : "text-white/70 hover:text-white hover:bg-visagio-teal/50"
        )}
      >
        <span className={cn("absolute left-0 top-1/2 h-6 w-1 -translate-y-1/2 rounded-r-full", visuals.accent.bg)} />
        <button
          type="button"
          onClick={handleChevronClick}
          aria-expanded={expanded}
          className={cn(
            "ml-1 flex h-8 w-7 shrink-0 items-center justify-center rounded-lg transition-colors",
            active
              ? "text-white hover:bg-white/10"
              : "text-white/60 hover:text-white hover:bg-visagio-teal/40"
          )}
        >
          {expanded ? (
            <ChevronDown className="h-3.5 w-3.5 transition-transform duration-200" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 transition-transform duration-200" />
          )}
        </button>

        <Link
          to={`/dashboard/project/${project.id}`}
          onClick={onNavigate}
          className="flex min-w-0 flex-1 cursor-pointer items-center gap-2.5 py-2.5 pr-3"
        >
          <div
            className={cn(
              "flex h-6 w-6 shrink-0 items-center justify-center rounded-md border",
              active
                ? "border-white/15 bg-white/10 text-white"
                : "border-white/10 bg-white/5 text-white/80"
            )}
          >
            <ProjectIcon
              className={cn(
                "h-3.5 w-3.5 transition-transform duration-200",
                !active && "group-hover:scale-110"
              )}
            />
          </div>
          <div className="min-w-0 flex-1">
            <span className="block truncate">{project.name}</span>
          </div>
        </Link>
      </div>

      {expanded && (
        <SidebarProjectSessions
          projectId={project.id}
          currentSessionId={currentSessionId}
          onNavigate={onNavigate}
        />
      )}
    </div>
  );
});

// Sessions list rendered below an expanded project
interface SidebarProjectSessionsProps {
  projectId: string;
  currentSessionId?: string;
  onNavigate?: () => void;
}

const SidebarProjectSessions = React.memo(function SidebarProjectSessions({
  projectId,
  currentSessionId,
  onNavigate,
}: SidebarProjectSessionsProps) {
  const { t } = useTranslation("sidebar");
  const { data: sessions = [], isLoading } = useSessionsQuery(projectId);

  return (
    <div className="mt-1 ml-4 pl-3 border-l border-visagio-teal/30 space-y-0.5">
      {isLoading ? (
        <div className="flex items-center gap-2 px-2 py-1.5 text-xs text-white/50 font-body">
          <div className="h-3 w-3 border-2 border-white/50 border-t-transparent rounded-full animate-spin" />
          <span>{t("loadingSessions")}</span>
        </div>
      ) : sessions.length === 0 ? (
        <p className="text-xs text-white/40 px-2 py-1.5 font-body italic">
          {t("noSessionsYet")}
        </p>
      ) : (
        sessions.map((session) => {
          const isActive = currentSessionId === session.id;
          const label = session.name?.trim() || t("untitledSession");
          return (
            <Link
              key={session.id}
              to={`/dashboard/project/${projectId}/session/${session.id}`}
              onClick={onNavigate}
              className={cn(
                "flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs font-body transition-all duration-200",
                isActive
                  ? "bg-visagio-teal/70 text-white shadow-sm"
                  : "text-white/60 hover:text-white hover:bg-visagio-teal/30"
              )}
              title={label}
            >
              <MessageSquare className="h-3 w-3 shrink-0" />
              <span className="truncate">{label}</span>
            </Link>
          );
        })
      )}
    </div>
  );
});
